import Utils from '@ambuliz/core/lib/utils/Utils';
import { BigNumber } from 'bignumber.js';
import moment from 'moment-timezone';
import { ZipCelXConfig, ZipCelXRow } from 'zipcelx';
import { Fare, FarePriceBreakdown, PriceBreakdownItem } from '../Models';
function formatPeriod(p?: 'regular' | 'night' | 'weekend') {
  if (!p) {
    return '';
  }
  switch (p) {
    case 'regular':
      return 'jour';
    case 'night':
      return 'nuit';
    case 'weekend':
      return 'weekend / JF';
  }
}

function formDateToDateString(d?: string): string | undefined {
  if (!d) {
    return undefined;
  }
  const date = moment(d);

  if (!date.isValid) {
    return undefined;
  }
  return date.format('DD/MM/YYYY');
}

function formDateToHourString(d?: string): string | undefined {
  if (!d) {
    return undefined;
  }
  const date = moment(d);

  if (!date.isValid) {
    return undefined;
  }
  return date.format('HH:mm');
}

class DataExportColumn {
  displayName: string | null = null;
  uniqueKey: string | null = null;
  private renderer: (object: FarePriceBreakdown) => string | number | undefined;

  constructor(
    displayName: string,
    renderer: (object: FarePriceBreakdown) => string | number | undefined,
    uniqueKey?: string
  ) {
    this.displayName = displayName;
    this.renderer = renderer;
    if (!this.uniqueKey) {
      this.uniqueKey = Utils.removeDiacriticsFrom(this.displayName).trim().replace(' ', '_').toLocaleLowerCase();
    }
  }

  render(object: FarePriceBreakdown): string {
    const result = this.renderer(object);
    if (result === undefined) {
      return '';
    } else if (typeof result == 'number') {
      return commaNumberFormat(result);
    } else {
      return result;
    }
  }
}

function capitalize(str: string) {
  var splitStr = str.toLowerCase().split(' ');
  for (var i = 0; i < splitStr.length; i++) {
    // You do not need to check if i is larger than splitStr length, as your for does that for you
    // Assign it back to the array
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  // Directly return the joined string
  return splitStr.join(' ');
}

function commaNumberFormat(nbr: number | string) {
  return ('' + nbr).replace('.', ',');
}
const Col = DataExportColumn;

const ExportColumns: DataExportColumn[] = [
  new Col('identifier', (f) => f.identifier),
  new Col('transportType', (f) => f.transportType),
  new Col('orderer', (f) => f.ordererId),
  new Col('pickedDepartureDate', (f) => formDateToDateString(f.pickedDepartureDate)),

  new Col('pickedDepartureHour', (f) => formDateToHourString(f.pickedDepartureDate)),
  new Col('pickedArrivalDate', (f) => formDateToDateString(f.pickedArrivalDate)),
  new Col('pickedArrivalHour', (f) => formDateToHourString(f.pickedArrivalDate)),
  new Col('departureFiness', (f) => f.departureHC),
  new Col('arrivalFiness', (f) => f.arrivalHC),
  new Col('sex', (f) => (f.patientSex === 'M' ? 'M.' : 'Mme')),
  new Col('patientName', (f) => f.patientLastname?.toUpperCase()),
  new Col('patientFirstname', (f) => capitalize(f.patientFirstname)),
  new Col('patientBirthname', (f) => f.patientBirthname?.toUpperCase()),
  new Col('patientBirthdate', (f) => formDateToDateString(f.patientBirthdate)),
  new Col('transporter', (f) => f.transporter),
  new Col('transporterName', (f) => f.transporterName),
  new Col('departurePlace', (f) => capitalize(f.departure.placeName)),
  new Col('departureFU', (f) => f.departure.serviceId),
  new Col('departureService', (f) => f.departure.service),
  new Col('departureRoom', (f) => f.departure.room),
  new Col('departureAddress', (f) => capitalize(f.departure.address)),
  new Col('departureZip', (f) => f.departure.zip),
  new Col('departureCity', (f) => capitalize(f.departure.city)),
  new Col('arrivalPlace', (f) => capitalize(f.arrival.placeName)),
  new Col('arrivalFU', (f) => f.arrival.serviceId),
  new Col('arrivalService', (f) => f.arrival.service),
  new Col('arrivalAddress', (f) => capitalize(f.arrival.address)),
  new Col('arrivalZip', (f) => f.arrival.zip),
  new Col('arrivalCity', (f) => capitalize(f.arrival.city)),
  new Col('pickedDistance', (f) => f.pickedDistance),
  new Col('pickedDistanceSource', (f) => {
    if (f.pickedDistanceSource === 'distancemap') {
      return 'distancier';
    } else {
      return 'googlemaps';
    }
  }),
  new Col('period', (f) => formatPeriod(f.timeType)),
  new Col('totalPrice', (f) => f.totalPrice),
  new Col('totalVAT', (f) => f.totalVAT),
  new Col('totalPriceWithVAT', (f) => f.totalPriceWithVAT),
  new Col('invoiceItems', (f) => f.priceBreakDown?.map((i) => i.title).join('\n')),

  new Col('invoiceQuantities', (f) => f.priceBreakDown?.map(() => '1').join('\n')),
  new Col('invoicePrices', (f) => f.priceBreakDown?.map((i) => commaNumberFormat(i.price)).join('\n')),
  new Col('invoiceVATRates', (f) =>
    f.priceBreakDown
      ?.map((i: PriceBreakdownItem) => new BigNumber(i.vatRate).multipliedBy(100).toFixed(2) + '%')
      .join('\n')
  ),
  new Col('invoiceVATs', (f) =>
    f.priceBreakDown
      ?.map((i: PriceBreakdownItem) =>
        commaNumberFormat(new BigNumber(i.price).multipliedBy(new BigNumber(i.vatRate)).toFixed(2))
      )
      .join('\n')
  ),
  new Col('invoicePricesWithVAT', (f) =>
    f.priceBreakDown
      ?.map((i: PriceBreakdownItem) =>
        commaNumberFormat(
          new BigNumber(i.price).multipliedBy(new BigNumber(i.vatRate)).plus(new BigNumber(i.price)).toFixed(2)
        )
      )
      .join('\n')
  ),
  new Col('totalPriceExceptExtras', (f) =>
    (
      f.priceBreakDown
        ?.filter((i: PriceBreakdownItem) => i.type !== 'extra' && i.type !== 'toll')
        .reduce((acc: BigNumber, item: PriceBreakdownItem) => {
          return acc.plus(new BigNumber(item.price));
        }, new BigNumber('0')) || new BigNumber('0')
    ).toFixed(2)
  ),

  new Col('totalDiscounts', (f) =>
    (
      f.priceBreakDown
        ?.filter((i: PriceBreakdownItem) => i.type === 'discount')
        .reduce((acc: BigNumber, item: PriceBreakdownItem) => {
          return acc.plus(new BigNumber(item.price));
        }, new BigNumber('0')) || new BigNumber('0')
    ).toFixed(2)
  ),
  new Col('qty', () => '1'),
];

export async function generateFaresExport(fares: Fare[]): Promise<ZipCelXConfig> {
  const headers = ExportColumns.map((c) => {
    return {
      value: c.displayName,
      type: 'string' as 'string' | 'number',
    } as any;
  });

  const rows = fares.map((f) => {
    return ExportColumns.map((c) => {
      return { value: c.render(f), type: 'string' as 'string' | 'number' };
    });
  });

  let dataSet: ZipCelXRow[] = [];
  dataSet.push(headers);
  dataSet = dataSet.concat(rows);

  const filename = `export-lcri-tarifs-${moment().format('YYYYMMDDHHmmss')}`;

  const config: ZipCelXConfig = {
    filename: filename,
    sheet: {
      data: dataSet,
    },
  };

  return config;
}
